import type { SpreadPageRect } from '@/lib/backgrounds/types/spread-page-rect'
import type { SpreadPageRects } from '@/lib/backgrounds/types/spread-page-rects'
import type { SpreadGeometryInput } from './types/spread-geometry-input'

/**
 * Page rects in the same canvas-pt coordinate space `documentCanvasRef` positions its own
 * (unscaled) children in — i.e. what `getCanvasPointFromPointerEvent` returns, and what
 * `DoubleSpreadDocument`/`SinglePageDocument` position their own `.doc-page-fill` at internally.
 *
 * Kept here as an independent, pure, testable function rather than exported from those two
 * components — mirror any change to their trim math here too: `double-spread-document.tsx`'s
 * `leftTrimLeft`/`pageTrimWidth`/`rightTrimLeft`, `single-page-document.tsx`'s
 * `halfSpreadCanvas`/`wrapperLeft` (`side==='right'` → `x = halfSpreadCanvas`, `side==='left'` →
 * `x = bleedMargin`, and `pageViewMode==='single-first'` maps to `side='right'`).
 */
export function computeSpreadPageRects(input: SpreadGeometryInput): SpreadPageRects {
  const {
    bleedWidth,
    pageTrimWidth,
    trimHeight,
    bleedMargin,
    spreadOriginCanvasX,
    singlePageSide,
  } = input

  if (singlePageSide) {
    const halfSpreadCanvas = Math.floor(bleedWidth / 2)
    const x = singlePageSide === 'first' ? halfSpreadCanvas : bleedMargin
    const rect: SpreadPageRect = { x, y: bleedMargin, width: pageTrimWidth, height: trimHeight }

    return singlePageSide === 'first' ? { left: null, right: rect } : { left: rect, right: null }
  }

  const leftTrimLeft = bleedMargin
  const rightTrimLeft = spreadOriginCanvasX ?? leftTrimLeft + pageTrimWidth

  return {
    left: { x: leftTrimLeft, y: bleedMargin, width: pageTrimWidth, height: trimHeight },
    right: { x: rightTrimLeft, y: bleedMargin, width: pageTrimWidth, height: trimHeight },
  }
}

/** The bounding rect spanning whichever of left/right actually exist — used to render/highlight a
 *  double-page background, which covers the full spread (or the single existing page, in a cover
 *  context where only one side is present). */
export function unionSpreadPageRect(rects: SpreadPageRects): SpreadPageRect | null {
  const { left, right } = rects

  if (left && right) {
    const x = Math.min(left.x, right.x)
    const y = Math.min(left.y, right.y)
    const maxX = Math.max(left.x + left.width, right.x + right.width)
    const maxY = Math.max(left.y + left.height, right.y + right.height)

    return { x, y, width: maxX - x, height: maxY - y }
  }

  return left ?? right
}

/**
 * Where a placed background paints: its slot's trim rect grown by `bleedMargin` on every outer
 * edge — top, bottom, and each outer side — but never across the spine. Matches legacy's
 * `ODBackground.drawSvg` / `RenderUtil.renderBackground` (page-editor `odeditorsvg.js` ~line 2514,
 * `odrender.js` ~line 435), which add `OUTER_MARGIN` (12pt) the same way. A left page's outer side
 * is its left edge, a right page's its right edge; a spread bleeds on the outer side of each page
 * that exists, so in a single-page cover view it still stops at the spine.
 *
 * Hit-testing and the drop-target highlight stay on the trim rects — legacy's `BackgroundSection`
 * highlight covers the trim only, too.
 */
export function backgroundBleedRect(
  slot: 'left' | 'right' | 'double',
  rects: SpreadPageRects,
  bleedMargin: number
): SpreadPageRect | null {
  const trim = slot === 'double' ? unionSpreadPageRect(rects) : rects[slot]

  if (!trim) return null

  const bleedLeft = rects.left != null && slot !== 'right' ? bleedMargin : 0
  const bleedRight = rects.right != null && slot !== 'left' ? bleedMargin : 0

  return {
    x: trim.x - bleedLeft,
    y: trim.y - bleedMargin,
    width: trim.width + bleedLeft + bleedRight,
    height: trim.height + bleedMargin * 2,
  }
}

/**
 * The box a placed background's IMAGE is fitted into, which `backgroundBleedRect` then clips.
 * Usually the bleed rect itself — except a spread (`double`) background on a one-page spread (the
 * book's first or last page). Legacy draws that at full spread size anyway (`ODBackground.drawSvg`,
 * page-editor `odeditorsvg.js` ~line 2514: always `width * 2 + OUTER_MARGIN * 2` wide, with no
 * page-count check), so the one page shows its own half of the image at spread scale and is filled
 * edge to edge: the first page (a right page) shows the right half, the last page (a left page)
 * the left half. Fitting the whole spread image into one page instead letterboxes it.
 */
export function backgroundImageRect(
  slot: 'left' | 'right' | 'double',
  rects: SpreadPageRects,
  bleedMargin: number
): SpreadPageRect | null {
  const bleed = backgroundBleedRect(slot, rects, bleedMargin)
  const { left, right } = rects

  if (!bleed || slot !== 'double' || (left && right)) return bleed

  const page = (left ?? right) as SpreadPageRect

  return {
    // The missing page sits on the spine side: left of a lone right page, right of a lone left one.
    x: (right ? page.x - page.width : page.x) - bleedMargin,
    y: page.y - bleedMargin,
    width: page.width * 2 + bleedMargin * 2,
    height: page.height + bleedMargin * 2,
  }
}
